import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Share, ThreeDots } from "react-bootstrap-icons";
import PageActions, { RefetchPages } from "./pageActions";
import { Link } from "react-router-dom";
import { DeletePage2MutationFn } from "../../../graphql/projects.hooks";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { useTranslation } from "react-i18next";
import {
  FolderDeleteMutationFn,
  FolderDuplicateMutationFn,
} from "../../../graphql/folder.hooks";
import { useUserQuery } from "../../../graphql/menu.hooks";
import { DomainProps, FolderProps, PageProps } from "./index";
import { IMoveItems } from "../../../types";
import { ITEMS } from "../../../utils";
import { useReactiveVar } from "@apollo/client";
import { isHaveBeenSaved, isLoadingOfDeletion } from "../../../graphql/state";
import { UserIcon } from "../../shadedPages/UserIcon/UserIcon";
import { CustomToast } from "../../../components/inputs/toast";

interface FolderPageListProps {
  projectAccessType?: DomainProps;
  currentProjectAccessType?: DomainProps;
  folders?: FolderProps[];
  pages?: PageProps[];
  folderImgSrc: string;
  pageImgSrc: string;
  deletePage?: DeletePage2MutationFn;
  deleteFolder?: FolderDeleteMutationFn;
  duplicateFolder?: FolderDuplicateMutationFn;
  domainId?: any;
  loading?: boolean;
  folderPageAccessType?: number;
  selectedItems?: IMoveItems;
  setSelectedItems?: React.Dispatch<React.SetStateAction<IMoveItems>>;
  refetchPagesByDomain?: RefetchPages;
  refetchAllPagesWithCredentials?: () => void;
  isAdminTable?: boolean;
}

const FolderPageListItemTable: React.FC<{
  projectAccessType?: DomainProps;
  currentProjectAccessType?: DomainProps;
  item: PageProps | FolderProps;
  imgSrc: string;
  type: "folder" | "page";
  deletePage?: DeletePage2MutationFn;
  deleteFolder?: FolderDeleteMutationFn;
  duplicateFolder?: FolderDuplicateMutationFn;
  refetchPagesByDomain: RefetchPages;
  domainId?: any;
  folderId?: number;
  pageId?: number;
  accessType?: number | string;
  folderPageAccessType?: number;
  selectedItems?: IMoveItems;
  setSelectedItems?: React.Dispatch<React.SetStateAction<IMoveItems>>;
  checkboxBorder: string;
  wasChecked?: number;
  setWasChecked?: React.Dispatch<React.SetStateAction<number>>;
  refetchAllPagesWithCredentials?: () => void;
  isAdminTable?: boolean;
}> = ({
  item,
  imgSrc,
  type,
  deletePage,
  domainId,
  folderId,
  deleteFolder,
  duplicateFolder,
  pageId,
  projectAccessType,
  folderPageAccessType,
  selectedItems,
  setSelectedItems,
  checkboxBorder,
  wasChecked,
  setWasChecked,
  refetchPagesByDomain,
  refetchAllPagesWithCredentials,
  isAdminTable = false,
}) => {
  const lng = useGetUrlLocale();
  const { t } = useTranslation();
  const { data: User } = useUserQuery();
  const isLoading = useReactiveVar(isLoadingOfDeletion);

  const link =
    type === "folder" && folderId && domainId
      ? `${lng}/projects/${domainId}/folder/${folderId}`
      : `${process.env.REACT_APP_CONSTRUCTOR_URL}${lng}/${item.id}`;

  const currentUser = User?.user?.email;

  const currentProjectAccessTypeIndex = projectAccessType?.find(
    (user) => user?.user?.email === currentUser
  );
  const currentProjectAccessType = currentProjectAccessTypeIndex?.accessType;

  const isActive =
    ITEMS.FOLDER === type
      ? selectedItems.folderIds.includes(item.id)
      : selectedItems.pageIds.includes(item.id);

  const changeCheckboxState = () => {
    if (isActive) {
      setWasChecked(item.id);
    } else {
      setWasChecked(null);
    }

    if (selectedItems.folderIds.includes(item.id) && ITEMS.FOLDER === type) {
      const updatedItems = selectedItems.folderIds.filter(
        (id) => id != item.id
      );
      setSelectedItems({
        pageIds: selectedItems.pageIds,
        folderIds: updatedItems,
      });
    } else if (
      !selectedItems.folderIds.includes(item.id) &&
      ITEMS.FOLDER === type
    ) {
      setSelectedItems({
        pageIds: selectedItems.pageIds,
        folderIds: [...selectedItems.folderIds, item.id],
      });
    }

    if (selectedItems.pageIds.includes(item.id) && ITEMS.PAGE === type) {
      const updatedItems = selectedItems.pageIds.filter((id) => id != item.id);
      setSelectedItems({
        folderIds: selectedItems.folderIds,
        pageIds: updatedItems,
      });
    } else if (
      !selectedItems.pageIds.includes(item.id) &&
      ITEMS.PAGE === type
    ) {
      setSelectedItems({
        folderIds: selectedItems.folderIds,
        pageIds: [...selectedItems.pageIds, item.id],
      });
    }
  };

  return (
    <>
      <td
        className={`ps-4 py-4 ${isActive && "bg-primary-light"} ${isLoading && "pe-none opacity-75"}`}
      >
        <div className={"form-check"}>
          <input
            onClick={changeCheckboxState}
            className={`form-check-input ${!isActive && checkboxBorder} ${wasChecked === item.id && "border-primary-subtle"}`}
            type="checkbox"
            checked={isActive}
            value={item.id}
          />
          <label className="form-check-label"></label>
        </div>
      </td>
      {!isAdminTable && (
        <td className={`py-0 ${isActive && "bg-primary-light"}`}>
          <Link
            target={type === "page" ? "_blank" : undefined}
            to={link}
            className={`page-prew-table d-flex justify-content-center overflow-hidden ${type === "page" ? "border rounded-1" : ""}`}
          >
            <img
              className="img-fluid h-100"
              src={
                type === "page" && (item as PageProps).image
                  ? (item as PageProps).image
                  : imgSrc
              }
              alt={item.title}
            />
          </Link>
        </td>
      )}
      <td className={`${isActive && "bg-primary-light"} table-title`}>
        {item.title.length > 20 ? `${item.title.slice(0, 20)}...` : item.title}{" "}
        <br />
        {type === "page" && (
          <>
            <span className="text-gray fs-7">{"URL: "}</span>
            <Link
              to={(item as PageProps).publicUrl}
              className="text-decoration-none text-gray fs-7"
            >
              {(item as PageProps).publicUrl}
            </Link>
          </>
        )}
      </td>
      <td className={`${isActive && "bg-primary-light"}`}>
        {type === "folder"
          ? "—"
          : (item as PageProps).publishedAt
            ? t("projects.publish")
            : t("projects.noPublish")}
      </td>
      <td className={`${isActive && "bg-primary-light"}`}>{item.id}</td>
      <td className={`${isActive && "bg-primary-light"}`}>
        <div className="d-flex flex-row-reverse justify-content-end">
          {item?.users?.map((user) => (
            <UserIcon key={user?.user?.email} email={user?.user?.email} />
          ))}
          <UserIcon
            email={"user" in item ? item?.user?.email : User?.user?.email}
            className="user-icon-owner"
          />
        </div>
      </td>
      <td className={`${isActive && "bg-primary-light"}`}>
        <div className="d-flex align-items-center actions-bar">
          <div>
            <Link
              to={`${lng}/user/access/${type === "folder" ? `folder/${folderId}` : `page/${pageId}`}/access`}
              className={`btn btn-outline-primary primary-hover rounded-1 settings-icon text-blue ${[...selectedItems.folderIds, ...selectedItems.pageIds].includes(item.id) && "pe-none btn-outline-gray text-gray"}`}
            >
              <Share className="fs-6" />
            </Link>
          </div>
          <Dropdown className="mx-3">
            <Dropdown.Toggle
              variant="outline-primary"
              id="dropdown-basic"
              className={`primary-hover ${[...selectedItems.folderIds, ...selectedItems.pageIds].includes(item.id) && "pe-none btn-outline-gray text-gray"}`}
            >
              <ThreeDots className="fs-6" />
            </Dropdown.Toggle>
            <PageActions
              type={type}
              deletePage={deletePage}
              deleteFolder={deleteFolder}
              duplicateFolder={duplicateFolder}
              refetchPagesByDomain={refetchPagesByDomain}
              itemId={item.id}
              pageId={pageId}
              domainId={domainId}
              accessType={currentProjectAccessType}
              folderPageAccessType={folderPageAccessType}
              refetchAllPagesWithCredentials={refetchAllPagesWithCredentials}
            />
          </Dropdown>
        </div>
      </td>
    </>
  );
};

const FolderPageListTable: React.FC<FolderPageListProps> = ({
  folders = [],
  pages = [],
  folderImgSrc,
  pageImgSrc,
  deletePage,
  duplicateFolder,
  deleteFolder,
  domainId,
  projectAccessType,
  selectedItems,
  setSelectedItems,
  refetchPagesByDomain,
  refetchAllPagesWithCredentials,
  isAdminTable = false,
}) => {
  const { t } = useTranslation();
  const haveBeenSaved = useReactiveVar(isHaveBeenSaved);
  const [checked, setChecked] = useState<boolean>(false);
  const [wasChecked, setWasChecked] = useState<number | null>(null);
  const [checkboxBorder, setCheckboxBorder] = useState<string>("border");

  useEffect(() => {
    if (!selectedItems.folderIds.length && !selectedItems.pageIds.length) {
      setChecked(false);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (selectedItems.folderIds.length || selectedItems.pageIds.length) {
      setCheckboxBorder("border-dark");
    }
  }, [selectedItems]);

  const selectedAllItemsOnPage = () => {
    if (checked) {
      setSelectedItems({ pageIds: [], folderIds: [] });
      setWasChecked(0);
      return;
    } else {
      setWasChecked(null);
    }
    const arrayOfFoldersId = folders.map((folder) => folder?.id);
    const arrayOfPagesId = pages.map((page) => page?.id);

    setSelectedItems({ pageIds: arrayOfPagesId, folderIds: arrayOfFoldersId });
  };

  return (
    <div className="col-12 table-scroll">
      <table className="folderPageTable table table-index mb-0 bg-white rounded overflow-y-hidden">
        <thead className="border-white rounded">
          <tr className="bg-disabled border table-heading">
            <th className="py-3 ps-4 col-1 bg-white">
              <div className="form-check">
                <input
                  onClick={selectedAllItemsOnPage}
                  className={`form-check-input ${!checked && checkboxBorder} ${wasChecked === 0 && "border-primary-subtle"}`}
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  type="checkbox"
                  value=""
                />
                <label className="form-check-label"></label>
              </div>
            </th>
            {!isAdminTable && (
              <th className="text-dark-gray bg-white py-3 col-1">
                {t("projects.tablePreview")}
              </th>
            )}
            <th className="text-dark-gray bg-white py-3 col-3">
              {t("projects.tableTitle")}
            </th>
            <th className="text-dark-gray bg-white py-3 col-2">
              {t("projects.tableStatus")}
            </th>

            <th className="text-dark-gray bg-white py-3 col-1">
              {t("projects.tableID")}
            </th>
            <th className="text-dark-gray bg-white py-3 col-2">
              {t("projects.tableUsers")}
            </th>
            <th className="text-dark-gray bg-white py-3 col-2">
              {t("projects.tableActions")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white border-white table-content listTable rounded">
          {folders.map((folder) => (
            <tr
              className={`align-middle ${
                selectedItems.folderIds.includes(folder.id) &&
                "outline-primary rounded"
              }`}
              key={folder.id}
            >
              <FolderPageListItemTable
                item={folder}
                imgSrc={folderImgSrc}
                type="folder"
                domainId={domainId}
                folderId={folder.id}
                refetchPagesByDomain={refetchPagesByDomain}
                projectAccessType={projectAccessType}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                checkboxBorder={checkboxBorder}
                wasChecked={wasChecked}
                setWasChecked={setWasChecked}
                isAdminTable={isAdminTable}
              />
            </tr>
          ))}
          {pages.map((page) => (
            <tr
              className={`align-middle ${
                selectedItems.pageIds.includes(page.id) &&
                "outline-primary rounded"
              }`}
              key={page.id}
            >
              <FolderPageListItemTable
                item={page}
                imgSrc={pageImgSrc}
                deletePage={deletePage}
                deleteFolder={deleteFolder}
                duplicateFolder={duplicateFolder}
                refetchPagesByDomain={refetchPagesByDomain}
                type="page"
                pageId={page.id}
                projectAccessType={projectAccessType}
                domainId={domainId}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                checkboxBorder={checkboxBorder}
                wasChecked={wasChecked}
                setWasChecked={setWasChecked}
                refetchAllPagesWithCredentials={refetchAllPagesWithCredentials}
                isAdminTable={isAdminTable}
              />
            </tr>
          ))}
        </tbody>
      </table>
      <CustomToast
        isShow={haveBeenSaved}
        setShow={isHaveBeenSaved}
        message={`${t("share.beenSaved")}`}
        isSeenClose={false}
        background="bg-success-light"
        text="text-success fw-400 fs-6"
        check={true}
      />
    </div>
  );
};

export default FolderPageListTable;
